import { useEffect, useRef, useState } from 'react'
import { SlidersHorizontal, Star, Image, ChevronDown, Zap, Award, X } from 'lucide-react'
import type { DirectorySortBy, DirectoryExtraFilters } from '../../lib/api'
import { CERT_OPTIONS } from '../../lib/directoryConstants'

interface Props {
  filters: DirectoryExtraFilters
  onChange: (filters: DirectoryExtraFilters) => void
  totalCount?: number
}

const SORT_OPTIONS: { value: DirectorySortBy; label: string }[] = [
  { value: 'relevance', label: 'Best match' },
  { value: 'rating',   label: 'Highest rated' },
  { value: 'reviews',  label: 'Most reviews' },
  { value: 'name',     label: 'A – Z' },
]


export function DirectoryFiltersBar({ filters, onChange, totalCount }: Props) {
  const [certOpen, setCertOpen] = useState(false)
  const [certSearch, setCertSearch] = useState('')
  const certRef = useRef<HTMLDivElement>(null)

  // Close cert dropdown on outside click
  useEffect(() => {
    if (!certOpen) return
    const handler = (e: MouseEvent) => {
      if (certRef.current && !certRef.current.contains(e.target as Node)) {
        setCertOpen(false)
        setCertSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [certOpen])

  const toggle = (key: 'hasRating' | 'hasPhotos' | 'emergencyOnly') => {
    onChange({ ...filters, [key]: !filters[key] })
  }

  const setCert = (cert: string | undefined) => {
    onChange({ ...filters, certification: cert })
    setCertOpen(false)
    setCertSearch('')
  }

  const setSort = (sortBy: DirectorySortBy) => {
    onChange({ ...filters, sortBy })
  }

  const filteredCerts = CERT_OPTIONS.filter(c =>
    c.toLowerCase().includes(certSearch.toLowerCase()),
  )

  const pillBase = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer select-none'
  const pillOn   = 'bg-trust-600 text-white border-trust-600'
  const pillOff  = 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'

  return (
    <div className="flex flex-wrap items-center gap-2 py-3">
      <SlidersHorizontal className="h-4 w-4 text-gray-400 flex-shrink-0" />

      {/* Toggle pills */}
      <button onClick={() => toggle('hasRating')} className={`${pillBase} ${filters.hasRating ? pillOn : pillOff}`}>
        <Star className="h-3.5 w-3.5" />
        Has rating
      </button>

      <button onClick={() => toggle('hasPhotos')} className={`${pillBase} ${filters.hasPhotos ? pillOn : pillOff}`}>
        <Image className="h-3.5 w-3.5" />
        Has photos
      </button>

      <button onClick={() => toggle('emergencyOnly')} className={`${pillBase} ${filters.emergencyOnly ? pillOn : pillOff}`}>
        <Zap className="h-3.5 w-3.5" />
        Emergency
      </button>

      {/* Certification combobox */}
      <div ref={certRef} className="relative">
        <button
          onClick={() => { setCertOpen(o => !o); setCertSearch('') }}
          className={`${pillBase} ${filters.certification ? pillOn : pillOff}`}
        >
          <Award className="h-3.5 w-3.5" />
          {filters.certification ?? 'Certification'}
          {filters.certification ? (
            <span
              role="button"
              aria-label="Clear certification"
              onClick={e => { e.stopPropagation(); setCert(undefined) }}
              className="ml-0.5 hover:opacity-70"
            >
              <X className="h-3 w-3" />
            </span>
          ) : (
            <ChevronDown className="h-3 w-3 opacity-60" />
          )}
        </button>

        {certOpen && (
          <div className="absolute left-0 top-full mt-1 z-30 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                placeholder="Search certifications…"
                value={certSearch}
                onChange={e => setCertSearch(e.target.value)}
                className="w-full px-2 py-1 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-trust-400"
              />
            </div>
            <ul className="max-h-52 overflow-y-auto py-1">
              {filteredCerts.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-400">No results</li>
              ) : filteredCerts.map(cert => (
                <li key={cert}>
                  <button
                    onClick={() => setCert(cert)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${filters.certification === cert ? 'font-semibold text-trust-600' : 'text-gray-700'}`}
                  >
                    {cert}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Result count + sort */}
      {totalCount !== undefined && totalCount > 0 && (
        <p className="text-sm text-gray-400 mr-1 hidden sm:block">
          {totalCount} result{totalCount !== 1 ? 's' : ''}
        </p>
      )}

      <div className="relative">
        <select
          value={filters.sortBy ?? 'relevance'}
          onChange={e => setSort(e.target.value as DirectorySortBy)}
          aria-label="Sort plumbers by"
          className="appearance-none pl-3 pr-8 py-1.5 rounded-full text-sm font-medium border border-gray-200 bg-white text-gray-700 hover:border-gray-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-trust-400"
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
      </div>
    </div>
  )
}
